import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { SectionList, View, } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import Carousel from 'react-native-snap-carousel';
import { AppView, withAnimatedNavigationHeader, SCREEN_BOTTOM_PADDING, rem, Spacer, } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAccountCallbacks from 'hooks/useAccountCallbacks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useStyles from 'hooks/useStyles';
import RowAccount from 'features/accounts/components/RowAccount';
import createStyleSheets from 'utils/createStyleSheets';
import { useFocusEffect } from 'hooks';
import ListEmptyView from 'design-system/ListEmptyView';
import { makeSelectOverviewHiddenSections, makeSelectOverviewClosedSections, ALL_DETAIL_TABS, } from '../selectors/overviewSections';
import DetailScreenPageControl from '../components/overview/DetailScreenPageControl';
import { isWeb } from '../../../constants';
const AnimatedCarousel = Animated.createAnimatedComponent(Carousel);
const keyExtractor = (item, index) => (item.id ?? index).toString();
const hiddenEverydaySelector = makeSelectOverviewHiddenSections('Everyday');
const hiddenSavingsSelector = makeSelectOverviewHiddenSections('Savings');
const hiddenInvestmentsSelector = makeSelectOverviewHiddenSections('Investments');
const hiddenAssetsSelector = makeSelectOverviewHiddenSections('Assets');
const hiddenDebtSelector = makeSelectOverviewHiddenSections('Debt');
const closedEverydaySelector = makeSelectOverviewClosedSections('Everyday');
const closedSavingsSelector = makeSelectOverviewClosedSections('Savings');
const closedInvestmentsSelector = makeSelectOverviewClosedSections('Investments');
const closedAssetsSelector = makeSelectOverviewClosedSections('Assets');
const closedDebtSelector = makeSelectOverviewClosedSections('Debt');
const Content = ({ type, route, onScroll, navigation }) => {
    const styles = useStyles(styleSet);
    const { contentWidth, paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const carouselRef = useRef(null);
    const scrollRef = useRef(null);
    const isHidden = type === 'hidden';
    const everydaySections = useAppSelector(isHidden ? hiddenEverydaySelector : closedEverydaySelector);
    const savingsSections = useAppSelector(isHidden ? hiddenSavingsSelector : closedSavingsSelector);
    const investmentsSections = useAppSelector(isHidden ? hiddenInvestmentsSelector : closedInvestmentsSelector);
    const assetsSections = useAppSelector(isHidden ? hiddenAssetsSelector : closedAssetsSelector);
    const debtSections = useAppSelector(isHidden ? hiddenDebtSelector : closedDebtSelector);
    const sectionsByTab = useMemo(() => ({
        Everyday: everydaySections,
        Savings: savingsSections,
        Investments: investmentsSections,
        Assets: assetsSections,
        Debt: debtSections,
    }), [
        assetsSections,
        debtSections,
        everydaySections,
        investmentsSections,
        savingsSections,
    ]);
    const availableTabs = useMemo(() => ALL_DETAIL_TABS.filter((tab) => sectionsByTab[tab].length > 0), [sectionsByTab]);
    const title = route.params?.title;
    const initialTitle = useRef(title);
    const [initialTabIndex, setInitialTabIndex] = useState(() => {
        if (!title)
            return 0;
        const idx = availableTabs.indexOf(title);
        return idx >= 0 ? idx : 0;
    });
    const didLoadData = useRef(!!availableTabs.length);
    useEffect(() => {
        if (!title || !isWeb || !availableTabs.length || didLoadData.current) {
            return;
        }
        didLoadData.current = true;
        const idx = availableTabs.indexOf(title);
        setInitialTabIndex(idx >= 0 ? idx : 0);
        // When triggering a page refresh on the web, we need to wait for data to be loaded to set the initial tab
    }, [availableTabs, didLoadData, title]);
    const scrollX = useSharedValue(initialTabIndex * contentWidth);
    const scrollHandle = useAnimatedScrollHandler((event) => {
        scrollX.value = event.contentOffset.x;
    });
    const tabItems = useMemo(() => availableTabs.map((tab) => ({
        label: tab,
        count: sectionsByTab[tab].reduce((sum, s) => sum + s.data.length, 0),
    })), [availableTabs, sectionsByTab]);
    const { onRowPress, onOpenLogins, onRowLongPress, onOpenUpgrade } = useAccountCallbacks(false);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, styles.scroll);
    const renderSectionHeader = useCallback(({ section }) => {
        if (!section.data.length || !section.title)
            return null;
        return (<>
          {!isHidden && section.showHiddenGroupHeader ? (<>
              <SectionHeader verticalMargins>Hidden accounts</SectionHeader>
              <Text TextThin-16 Secondary>
                Hidden accounts’ history aren’t included in the Overview
              </Text>
              <Spacer.H16 />
            </>) : null}
          <View style={styles.sectionCardHeader}>
            <Text Text-16 OffBlack>
              {section.title}
            </Text>
          </View>
        </>);
    }, [isHidden, styles.sectionCardHeader]);
    const renderItem = useCallback(({ item, index, section }) => {
        const isLast = index === section.data.length - 1;
        return (<View style={[styles.cardItem, isLast ? styles.cardItemLast : null]}>
          <RowAccount data={item} onPress={onRowPress} onRowLongPressed={onRowLongPress} onOpenLogins={onOpenLogins} onOpenUpgrade={onOpenUpgrade} style={styles.cardRow} onCard showBalance={isHidden}/>
        </View>);
    }, [
        isHidden,
        onOpenLogins,
        onOpenUpgrade,
        onRowLongPress,
        onRowPress,
        styles.cardItem,
        styles.cardItemLast,
        styles.cardRow,
    ]);
    const getItemLayout = useCallback((_data, index) => ({
        length: contentWidth,
        offset: contentWidth * index,
        index,
    }), [contentWidth]);
    const carouselRenderItem = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ item }) => (<SectionList scrollEnabled={false} initialNumToRender={50} stickySectionHeadersEnabled={false} sections={sectionsByTab[item]} contentContainerStyle={{
            paddingHorizontal: paddingHorizontal || rem(16),
        }} renderItem={renderItem} renderSectionHeader={renderSectionHeader} keyExtractor={keyExtractor}/>), [sectionsByTab, paddingHorizontal, renderItem, renderSectionHeader]);
    const subtitle = isHidden
        ? 'Hidden accounts’ balances aren’t included in the Overview'
        : 'Closed accounts no longer sync, but their history remains included in your Overview unless you hide them';
    const onChangeTab = useCallback((key) => {
        navigation.setParams({ title: key });
    }, []);
    /**
     * setInitialTabIndex when leaving the screen on the web to land on
     * the same tab when navigating back
     */
    useFocusEffect(useCallback(() => () => {
        if (didLoadData.current && !availableTabs.length) {
            if (navigation.canGoBack()) {
                navigation.pop();
            }
            else {
                navigation.navigate('Feed');
            }
            return;
        }
        if (!navigation.isFocused() &&
            isWeb &&
            title &&
            title !== initialTitle.current) {
            const index = availableTabs.indexOf(title);
            if (index >= 0) {
                setInitialTabIndex(index);
            }
        }
    }, [availableTabs, title]));
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <Animated.ScrollView onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle}>
        <ScreenInfoHeader subTitle={subtitle} style={[styles.header, paddingHorizontalStyle]}/>

        {availableTabs.length > 1 ? (<DetailScreenPageControl tabs={tabItems} scrollX={scrollX} carouselRef={carouselRef} scrollRef={scrollRef} onChange={onChangeTab}/>) : null}
        {!availableTabs.length && (<ListEmptyView onCard happy title="There are no accounts" style={{ marginHorizontal: paddingHorizontal }}/>)}
        <AnimatedCarousel data={availableTabs} renderItem={carouselRenderItem} sliderWidth={contentWidth} itemWidth={contentWidth} firstItem={initialTabIndex} onScroll={scrollHandle} disableInitialSnap inactiveSlideScale={1} inactiveSlideOpacity={1} useExperimentalSnap keyExtractor={(item) => item} getItemLayout={getItemLayout} ref={carouselRef} 
    // @ts-expect-error -- the carousel passes ScrollView props on to its FlatList
    scrollEventThrottle={16} decelerationRate="fast" disableIntervalMomentum useNativeOnScroll removeClippedSubviews={false}/>
      </Animated.ScrollView>
    </AppView>);
};
export const HiddenAccountsOverviewScreen = withAnimatedNavigationHeader((props) => <Content type="hidden" {...props}/>, ({ colors }) => ({
    title: 'Hidden accounts',
    headerTintColor: colors.text.primary,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export const ClosedAccountsOverviewScreen = withAnimatedNavigationHeader((props) => <Content type="closed" {...props}/>, ({ colors }) => ({
    title: 'Closed accounts',
    headerTintColor: colors.text.primary,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    scroll: {
        minHeight: '100%',
    },
    sectionCardHeader: {
        backgroundColor: colors.cards.onDark,
        padding: rem(16),
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        borderBottomWidth: rem(1),
        borderBottomColor: colors.background.dark,
    },
    cardItem: {
        backgroundColor: colors.cards.onDark,
    },
    cardItemLast: {
        marginBottom: rem(16),
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
    },
    cardRow: {
        paddingHorizontal: rem(16),
    },
    header: {
        paddingBottom: rem(16),
    },
}));
